import { FC } from 'react';
import { FileText, Printer } from 'lucide-react';

import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
// Leaf import, not the barrel: the barrel re-exports the hook, which reaches services/api and
// Firebase behind it. This component fetches nothing and must not pull that graph in.
import { canPrint } from 'features/common/hooks/usePrintTickets/ticketPdfEndpoints';
import { PrintableTickets, PrintFormat } from 'features/common/hooks/usePrintTickets/types';

import styles from './PunchupPrintTicketButtons.module.css';

export interface PrintTicketLabels {
	thermal: string;
	standard: string;
	thermalHint: string;
	standardHint: string;
	/** Shown when that format cannot be produced. Per format — the reasons differ: a record with
	 *  ticket ids but no transaction can still print thermal, so a shared string lies about one. */
	thermalUnavailable: string;
	standardUnavailable: string;
}

export interface PunchupPrintTicketButtonsProps {
	tickets: PrintableTickets;
	printing?: PrintFormat | null;
	/** True while any print on the page is in flight, so the rest wait their turn. */
	busy?: boolean;
	size?: 'default' | 'small';
	onPrint: (format: PrintFormat) => void;
	labels: PrintTicketLabels;
}

export const PunchupPrintTicketButtons: FC<PunchupPrintTicketButtonsProps> = ({
	tickets,
	printing = null,
	busy = false,
	size = 'default',
	onPrint,
	labels,
}) => {
	const smallClass = size === 'small' ? styles.smallButton : undefined;
	const iconSize = size === 'small' ? 14 : 16;

	const formats: {
		format: PrintFormat;
		label: string;
		hint: string;
		unavailable: string;
		icon: JSX.Element;
	}[] = [
		{
			format: 'thermal',
			label: labels.thermal,
			hint: labels.thermalHint,
			unavailable: labels.thermalUnavailable,
			icon: <Printer size={iconSize} aria-hidden />,
		},
		{
			format: 'standard',
			label: labels.standard,
			hint: labels.standardHint,
			unavailable: labels.standardUnavailable,
			icon: <FileText size={iconSize} aria-hidden />,
		},
	];

	return (
		<div className={styles.root}>
			{formats.map(({ format, label, hint, unavailable, icon }) => {
				const available = canPrint(tickets, format);
				const isPrintingThis = printing === format;
				return (
					<PunchupButton
						key={format}
						// PunchupButton spreads props onto a bare <button>, which inside a <form> is an
						// implicit submit. A print control must never submit the form it sits in.
						type="button"
						variant="outline"
						className={smallClass}
						icon={icon}
						isLoading={isPrintingThis}
						disabled={!available || (busy && !isPrintingThis)}
						title={available ? hint : unavailable}
						onClick={() => onPrint(format)}
					>
						{label}
					</PunchupButton>
				);
			})}
		</div>
	);
};
